import { format, startOfMonth } from "date-fns";
import type { SessionWithDetails } from "@/features/sessions/types";

/**
 * Get the start and end date range for a given month
 * @param year - Full year (e.g., 2026)
 * @param month - Month number 1-12 (January = 1, December = 12)
 * @returns Object with start and end dates for the month
 */
export function getMonthDateRange(year: number, month: number): { start: Date; end: Date } {
    const start = new Date(year, month - 1, 1, 0, 0, 0, 0);
    const end = new Date(year, month, 0, 23, 59, 59, 999);
    return { end, start };
}

/**
 * Format session date and time with venue name
 * Format: "Thursday, January 2, 2026 at 7:00 PM • Flora Lounge"
 * @param date - Session date
 * @param venueName - Name of the venue
 * @returns Formatted string
 */
export function formatSessionDateTime(date: Date, venueName: string): string {
    const dateStr = format(date, "EEEE, MMMM d, yyyy");
    return `${dateStr} • ${venueName}`;
}

/**
 * Check if all sessions are in the future (scheduledAt > current date/time)
 * @param sessions - Array of sessions to check
 * @returns true if all sessions are in the future, false otherwise
 */
export function areSessionsFuture(sessions: SessionWithDetails[]): boolean {
    if (sessions.length === 0) {
        return false;
    }

    const now = new Date();

    return sessions.every((session) => {
        const scheduledDate = new Date(session.scheduledAt);
        return scheduledDate > now;
    });
}

/**
 * Check if a given month/year is strictly in the future.
 * @param year - Full year (e.g., 2026)
 * @param month - Month number 1-12 (January = 1, December = 12)
 * @param now - Optional current date for deterministic tests
 * @returns true when target month is after the current month, false otherwise
 */
export function isFutureMonth(year: number, month: number, now: Date = new Date()): boolean {
    const targetMonthStart = startOfMonth(new Date(year, month - 1, 1));
    const currentMonthStart = startOfMonth(now);
    return targetMonthStart > currentMonthStart;
}
